
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, Clock, Circle, Package, CreditCard, Mail, CheckCircle2, FileDown, AlertTriangle, Trash2 } from "lucide-react";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { computeOrderFinancials } from "@/lib/orderPdfCalc";
import { useOrderWorkflow, useAdvanceOrderWorkflow } from "@/hooks/use-order-workflow";
import { useUpdateSaleStatus } from "@/hooks/use-orders";
import { useUpdateSkipAllocation } from "@/hooks/use-update-skip-allocation";
import { DeviceAllocationSection } from "./DeviceAllocationSection";
import { useDeviceAllocation } from "@/hooks/use-device-allocation";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { CreateCreditPackageButton } from "./CreateCreditPackageButton";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useDeliveryNotes, useCreateDeliveryNote } from "@/hooks/use-delivery-notes";
import { ShipmentManagementSection } from "./ShipmentManagementSection";
import { useOrderShipments, OrderShipment } from "@/hooks/use-order-shipments";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { DeliveryAddressBlock } from "./DeliveryAddressBlock";
import { COMPANY_ADDRESS_LINES, COMPANY_NAME, COMPANY_PDF_FOOTER } from "@/constants/company";


interface OrderWorkflowProps {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DeviceAllocation {
  deviceId: string;
  deviceName?: string;
  allocatedCredits: number;
}

export const OrderWorkflow = ({
  orderId,
  open,
  onOpenChange
}: OrderWorkflowProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: workflowData, isLoading } = useOrderWorkflow(orderId);
  const { data: shipments = [] } = useOrderShipments(orderId);
  const hasShipments = shipments.length > 0;
  const advanceWorkflow = useAdvanceOrderWorkflow();
  const updateSaleStatus = useUpdateSaleStatus();
  const updateSkipAllocation = useUpdateSkipAllocation();
  const { saveDeviceAllocation } = useDeviceAllocation();
  const { data: deliveryNotes } = useDeliveryNotes(orderId);
  const createDeliveryNote = useCreateDeliveryNote();
  const [isDeviceAllocationExpanded, setIsDeviceAllocationExpanded] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailJustSent, setEmailJustSent] = useState(false);
  const [isGeneratingDeliveryNote, setIsGeneratingDeliveryNote] = useState(false);
  const [isGeneratingShipmentDeliveryNote, setIsGeneratingShipmentDeliveryNote] = useState<string | null>(null);
  const { isSystemAdmin } = useSimpleRole();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteOrder = async () => {
    if (!workflowData?.order) return;
    const order = workflowData.order;
    setIsDeleting(true);
    try {
      // 1. Delete credit packages (no cascade from orders)
      const { error: pkgErr } = await supabase
        .from('device_credit_packages')
        .delete()
        .eq('order_id', orderId);
      if (pkgErr) throw pkgErr;

      // 2. Delete order (cascades delivery_notes + order_shipments)
      const { error: orderErr } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);
      if (orderErr) throw orderErr;

      // 3. Delete sale only if no other orders reference it
      const saleId = order.sale?.id;
      if (saleId) {
        const { data: remaining, error: checkErr } = await supabase
          .from('orders')
          .select('id')
          .eq('sale_id', saleId)
          .limit(1);
        if (checkErr) throw checkErr;
        if (!remaining || remaining.length === 0) {
          const { error: saleErr } = await supabase
            .from('sales')
            .delete()
            .eq('id', saleId);
          if (saleErr) throw saleErr;
        }
      }

      toast({ title: 'Order deleted', description: 'Order, credit packages and sale removed. Deal preserved.' });
      queryClient.invalidateQueries({ queryKey: ['order-management'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['reserved-credit-packages'] });
      queryClient.invalidateQueries({ queryKey: ['order-year-breakdown'] });
      queryClient.invalidateQueries({ queryKey: ['enhanced-credit-packages'] });
      queryClient.invalidateQueries({ queryKey: ['kpi-metrics'] });
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
      onOpenChange(false);
    } catch (err: any) {
      console.error('Failed to delete order:', err);
      toast({
        title: 'Delete failed',
        description: err?.message || 'Could not delete order. Credits may already be drawn from packages.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const generateOrderSummaryPDF = async (order: any) => {
    try {
      // First, fetch the deal data by following the chain: order -> sale -> deal
      const { data: sale, error: saleError } = await supabase
        .from('sales')
        .select('deal_id')
        .eq('id', order.sale?.id)
        .maybeSingle();

      if (saleError) {
        console.error('Error fetching sale:', saleError);
        toast({
          title: "Error",
          description: "Failed to fetch order details. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (!sale || !sale.deal_id) {
        toast({
          title: "Error",
          description: "No deal found for this order.",
          variant: "destructive",
        });
        return;
      }

      // Now fetch the full deal details
      const { data: deal, error: dealError } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(
            id,
            first_name,
            last_name,
            email,
            organization:organizations(name)
          ),
          organization:organizations(name),
          assigned_staff:crm_staff!crm_deals_assigned_to_staff_id_fkey(first_name, last_name),
          created_by_staff:crm_staff!crm_deals_created_by_staff_id_fkey(first_name, last_name),
          assigned_partner:crm_partners!crm_deals_assigned_to_partner_id_fkey(first_name, last_name),
          created_by_partner:crm_partners!crm_deals_created_by_partner_id_fkey(first_name, last_name)
        `)
        .eq('id', sale.deal_id)
        .maybeSingle();

      if (dealError) {
        console.error('Error fetching deal:', dealError);
        toast({
          title: "Error",
          description: "Failed to fetch deal details. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (!deal) {
        toast({
          title: "Error",
          description: "Deal details not found.",
          variant: "destructive",
        });
        return;
      }

      // Now generate the comprehensive PDF using the DealCalculator logic
      const doc = new jsPDF({ compress: true });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Helper function to format currency with thousand separators
      const formatCurrency = (amount: number): string => {
        return amount.toLocaleString('en-GB', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        });
      };
      
      // Function to generate the PDF content
      const generatePDFContent = () => {
        // Extract deal data + compute financials using the shared helper so the
        // PDF matches the on-screen DealCalculator totals exactly.
        const dealData = (deal.deal_data as any) || {};
        const fin = computeOrderFinancials(deal);
        const {
          orderType,
          paymentType,
          cameras,
          totalCreditsNeeded,
          pricePerImage,
          cameraPrice,
          cameraCost,
          accessoriesCost,
          dealAccessories,
          cameraModel,
          currentPlan,
          coverageYears,
          annualCredits,
          annualCreditsGross,
          annualCreditsNet,
          discountRows,
          subtotalGross,
          year1Total,
          finalTotal,
          contractValue,
          averageCostPerDay,
        } = fin;

        // Main Header - Check if deal has order reference
        doc.setFontSize(28);
        doc.setTextColor(0, 0, 0);
        const hasOrderReference = deal.order_reference;
        const documentTitle = hasOrderReference ? 'ORDER SUMMARY' : 'QUOTE';
        doc.text(documentTitle, pageWidth - (hasOrderReference ? 105 : 60), 25);
        
        // Two-column header layout
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        
        // Left Column - Company Details
        let leftY = 40;
        doc.setFont(undefined, 'bold');
        doc.text(COMPANY_NAME, 20, leftY);
        doc.setFont(undefined, 'normal');
        COMPANY_ADDRESS_LINES.forEach((line) => {
          doc.text(line, 20, leftY += 5);
        });
        
        // Right Column - Quote Details
        const rightX = pageWidth - 80;
        let rightY = 40;
        
        const currentDate = new Date().toLocaleDateString('en-GB', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        });
        const expiryDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        });
        
        doc.text(`Date: ${currentDate}`, rightX, rightY);
        doc.text(`Expiry: ${expiryDate}`, rightX, rightY += 5);
        const quoteNumber = deal.quote_number || `QU-${new Date().getFullYear()}-PENDING`;
        doc.text(`Quote Number: ${quoteNumber}`, rightX, rightY += 5);
        doc.text(`Reference: ${deal.order_reference || ''}`, rightX, rightY += 5);
        
        // Customer Details Section - Show who the order is for
        let customerY = rightY + 10;
        doc.setFont(undefined, 'bold');
        doc.text('Order For:', 20, customerY);
        doc.setFont(undefined, 'normal');
        
        // Show contact name first
        if (deal.contact) {
          const contact = deal.contact;
          doc.text(`${contact.first_name || ''} ${contact.last_name || ''}`.trim(), 20, customerY += 5);
        }
        
        // Show organization from deal
        if (deal.organization?.name) {
          doc.text(deal.organization.name, 20, customerY += 5);
        }
        
        // Show contact email
        if (deal.contact?.email) {
          doc.text(deal.contact.email, 20, customerY += 5);
        }
        
        // Update rightY for subsequent content
        rightY = customerY;
        
        // Order Summary Section
        const summaryY = Math.max(leftY, rightY) + 15;
        doc.setFont(undefined, 'bold');
        doc.setFontSize(11);
        doc.text('Order Summary', 20, summaryY);
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text(`Plan: ${currentPlan.name} (${currentPlan.duration} year${currentPlan.duration > 1 ? 's' : ''})`, 20, summaryY + 8);
        doc.text(`Payment: ${paymentType === 'annual' ? 'Annual Contract' : 'Upfront Payment'}`, 20, summaryY + 13);
        doc.text(`Per-image cost: £${pricePerImage.toFixed(2)}`, rightX, summaryY + 8);
        doc.text(`Average daily cost/camera: £${averageCostPerDay.toFixed(2)}`, rightX, summaryY + 13);
        
        // Build comprehensive table data with line items and financial summary
        const tableData = [];
        
        // Add line items
        if (orderType === 'cameras_and_credits') {
          // Camera line item
          tableData.push([
            cameraModel,
            cameras.toFixed(2),
            `£${formatCurrency(cameraPrice)}`,
            `£${formatCurrency(cameraCost)}`
          ]);

          // Credits line item — skipped entirely for Trial (Pilot Pack) deals
          if (currentPlan.name !== 'Trial') {
            let creditDescription = '';
            if (paymentType === 'annual') {
              creditDescription = `${currentPlan.name} Plan ${annualCredits.toLocaleString()} Images and ${currentPlan.duration} Year On Air`;
            } else {
              creditDescription = `${currentPlan.name} Plan ${totalCreditsNeeded.toLocaleString()} Images and ${currentPlan.duration} Year${currentPlan.duration > 1 ? 's' : ''} On Air`;
            }

            // Show GROSS credits on the line; per-image discount is surfaced as its own row below.
            const creditUnitPrice = paymentType === 'annual' ? annualCreditsGross : annualCreditsGross * coverageYears;
            const creditQuantity = cameras;

            tableData.push([
              creditDescription,
              creditQuantity.toFixed(2),
              `£${formatCurrency(creditUnitPrice / creditQuantity)}`,
              `£${formatCurrency(creditUnitPrice)}`
            ]);
          }
        } else {
          // Credits only
          let creditDescription = '';
          if (paymentType === 'annual') {
            creditDescription = `${currentPlan.name} Plan ${annualCredits.toLocaleString()} Images and ${currentPlan.duration} Year On Air (Annual)`;
          } else {
            creditDescription = `${currentPlan.name} Plan ${totalCreditsNeeded.toLocaleString()} Images and ${currentPlan.duration} Year${currentPlan.duration > 1 ? 's' : ''} On Air`;
          }
          const creditsGross = paymentType === 'annual' ? annualCreditsGross : annualCreditsGross * coverageYears;
          tableData.push([
            creditDescription,
            '1.00',
            `£${formatCurrency(creditsGross)}`,
            `£${formatCurrency(creditsGross)}`
          ]);
        }

        // Add accessory line items from deal_data
        dealAccessories.forEach((acc: any) => {
          if (acc && acc.quantity > 0) {
            tableData.push([
              acc.name || 'Accessory',
              Number(acc.quantity).toFixed(2),
              `£${formatCurrency(Number(acc.unit_price) || 0)}`,
              `£${formatCurrency((Number(acc.quantity) || 0) * (Number(acc.unit_price) || 0))}`
            ]);
          }
        });

        // Add financial summary rows to the same table

        // Subtotal row (gross, before any discounts)
        tableData.push(['', '', 'Subtotal:', `£${formatCurrency(subtotalGross)}`]);

        // One row per active discount (camera, image, trial, accessories)
        discountRows.forEach(d => {
          tableData.push(['', '', `${d.label}:`, `-£${formatCurrency(d.amount)}`]);
        });

        // Payment structure specific rows
        if (paymentType === 'annual' && !fin.isTrial) {
          tableData.push(['', '', 'Year 1 Total:', `£${formatCurrency(year1Total)}`]);
          if (coverageYears > 1) {
            tableData.push(['', '', 'Annual Payment:', `£${formatCurrency(annualCreditsNet)}`]);
          }
          tableData.push(['', '', 'Contract Value:', `£${formatCurrency(contractValue)}`]);
        } else {
          tableData.push(['', '', 'Total:', `£${formatCurrency(finalTotal)}`]);
        }
        
        // Generate unified accounting table
        (doc as any).autoTable({
          startY: summaryY + 20,
          head: [['Description', 'Qty', 'Unit Price', 'Total']],
          body: tableData,
          styles: {
            fontSize: 8,
            cellPadding: 3,
            lineColor: [255, 255, 255],
            lineWidth: 0,
            halign: 'left',
          },
          headStyles: {
            fillColor: [240, 240, 240],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            lineColor: [255, 255, 255],
            lineWidth: 0,
            halign: 'left',
          },
          bodyStyles: {
            lineColor: [255, 255, 255],
            lineWidth: 0,
            halign: 'left',
          },
          columnStyles: {
            0: { cellWidth: 90, halign: 'left' },
            1: { cellWidth: 25, halign: 'left' },
            2: { cellWidth: 35, halign: 'left' },
            3: { cellWidth: 35, halign: 'left' }
          },
          didParseCell: function(data: any) {
            // Style financial summary rows
            const rowIndex = data.row.index;
            const isFinancialRow = rowIndex >= (orderType === 'cameras_and_credits' ? 3 : 2);
            const isEmptyRow = data.cell.text[0] === '' && data.column.index === 0 && rowIndex >= (orderType === 'cameras_and_credits' ? 2 : 1);
            const isTotalRow = data.cell.text[0] && (data.cell.text[0].includes('Total:') || data.cell.text[0].includes('Contract Value:'));
            
            if (isEmptyRow) {
              data.cell.styles.lineWidth = 0;
              data.cell.styles.fillColor = [255, 255, 255];
            } else if (isFinancialRow && !isEmptyRow) {
              if (data.column.index >= 2) {
                data.cell.styles.fontStyle = isTotalRow ? 'bold' : 'normal';
                data.cell.styles.fontSize = isTotalRow ? 9 : 8;
                data.cell.styles.lineWidth = 0;
                if (data.cell.text[0] && data.cell.text[0].includes('Discount')) {
                  data.cell.styles.textColor = [200, 0, 0];
                }
              } else {
                data.cell.styles.lineWidth = 0;
                data.cell.text = [''];
              }
            } else {
              data.cell.styles.lineWidth = 0;
            }
          },
          margin: { left: 20, right: 20 },
          tableLineColor: [255, 255, 255],
          tableLineWidth: 0,
          theme: 'plain'
        });
        
        // Add note below table
        let currentY = (doc as any).lastAutoTable.finalY + 5;
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('All amounts are ex VAT', 20, currentY);
        currentY += 8;
        
        // Terms Section
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Basic Terms', 20, currentY);
        currentY += 8;
        
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        
        const effectiveCameraQuantity = orderType === 'cameras_and_credits' ? cameras : 0;
        
        const terms = [
          `On Air Period – Refers to the contracted service duration of ${currentPlan.duration} year${currentPlan.duration > 1 ? 's' : ''} during which camera connectivity is maintained and images are delivered.`,
          '',
          'Additional Credits – May be purchased at any time at the per-image rate of the originally selected package. Minimum purchase thresholds apply for extending the On Air period.',
          '',
          'Included Features – Image credit pricing includes privacy filtering, AI-driven image search*, and optional real-time notifications.',
          '',
          paymentType === 'annual'
            ? `Payment Structure – Annual Contract: Year 1 payment of £${formatCurrency(year1Total)} includes camera${cameras > 1 ? 's' : ''} and first year credits. Subsequent years: £${formatCurrency(annualCreditsNet)} annually for ${currentPlan.duration - 1} year${currentPlan.duration - 1 > 1 ? 's' : ''}.`
            : 'Payment Options – Full Upfront Payment: Provides full access to all purchased credits for the entire coverage period.',
          '',
          'Image Retention – Images are stored on the Snapshot Live platform for 90 days. Customers may extend storage by ingesting images into their own system via API**.',
          '',
          '*see full terms and conditions for details',
          '**API integration cost POA'
        ];
        
        terms.forEach(term => {
          if (term === '') {
            currentY += 3;
          } else {
            const lines = doc.splitTextToSize(term, pageWidth - 40);
            doc.text(lines, 20, currentY);
            currentY += lines.length * 3.5 + 1;
          }
        });
        
        // Footer
        doc.setFontSize(8);
        doc.text(doc.splitTextToSize(COMPANY_PDF_FOOTER, pageWidth - 40), 20, doc.internal.pageSize.getHeight() - 13);
        
        // Save PDF
        const contact = deal.contact;
        const filePrefix = hasOrderReference ? 'Order_Summary' : 'Quote';
        const quoteRef = deal.quote_number || `TEMP-${Date.now().toString().slice(-4)}`;
        const fileName = `${filePrefix}_${quoteRef}_${contact ? `${contact.first_name}_${contact.last_name}` : deal.title?.replace(/\s+/g, '_') || order.id.slice(0, 8)}.pdf`;
        doc.save(fileName);
        
        toast({
          title: `${hasOrderReference ? 'Order Summary' : 'Quote'} Downloaded`,
          description: `Professional ${hasOrderReference ? 'order summary' : 'quotation'} saved as ${fileName}`,
        });
      };
      
      // Try to add logo, fallback to text if failed
      const logoImg = document.createElement('img');
      logoImg.onload = function() {
        try {
          // Downscale logo to keep PDF small (source is 4321x1239)
          const targetW = 480;
          const scale = targetW / logoImg.width;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas context not available');
          canvas.width = targetW;
          canvas.height = Math.round(logoImg.height * scale);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(logoImg, 0, 0, canvas.width, canvas.height);
          const logoDataUrl = canvas.toDataURL('image/jpeg', 0.85);

          const logoWidth = 60;
          const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
          doc.addImage(logoDataUrl, 'JPEG', 20, 15, logoWidth, logoHeight, undefined, 'FAST');
          
          generatePDFContent();
        } catch (logoError) {
          console.warn('Failed to add logo:', logoError);
          generatePDFContent();
        }
      };
      logoImg.onerror = function() {
        console.warn('Failed to load logo image');
        generatePDFContent();
      };
      logoImg.src = '/snapshot-logo.png';
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate order summary PDF. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading || !workflowData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const { order, steps } = workflowData;
  
  // Determine order type and workflow behavior
  const isCreditOnly = order.order_type === 'credits_only';
  const showDeviceAllocation = !order.skip_device_allocation;

  // Check if device allocation is complete (only relevant for non-skipped allocation)
  // Debug logging to understand the issue
  console.log("🔍 [OrderWorkflow Debug] Order ID:", orderId);
  console.log("🔍 [OrderWorkflow Debug] allocated_device_ids:", order.allocated_device_ids, "Type:", typeof order.allocated_device_ids);
  console.log("🔍 [OrderWorkflow Debug] allocated_device_ids length:", order.allocated_device_ids?.length);
  console.log("🔍 [OrderWorkflow Debug] showDeviceAllocation:", showDeviceAllocation);
  console.log("🔍 [OrderWorkflow Debug] skip_device_allocation:", order.skip_device_allocation);
  console.log("🔍 [OrderWorkflow Debug] order_data device_allocations:", (order.order_data as any)?.device_allocations);
  
  // Strengthened device allocation check with multiple fallback conditions
  const isDeviceAllocationComplete = (() => {
    // If allocation is skipped, always consider it complete
    if (order.skip_device_allocation) {
      console.log("🔍 [OrderWorkflow Debug] Allocation skipped, marking as complete");
      return true;
    }
    
    // NEW: If shipments exist, user is using the incremental allocation path
    // This is a valid alternative to upfront allocation
    if (hasShipments) {
      console.log("🔍 [OrderWorkflow Debug] Shipments exist, using incremental allocation path");
      return true;
    }
    
    // Primary check: allocated_device_ids array
    if (Array.isArray(order.allocated_device_ids) && order.allocated_device_ids.length > 0) {
      console.log("🔍 [OrderWorkflow Debug] Found allocated_device_ids array with length:", order.allocated_device_ids.length);
      return true;
    }
    
    // Fallback check: order_data.device_allocations
    const orderData = order.order_data as any;
    if (orderData?.device_allocations && Array.isArray(orderData.device_allocations) && orderData.device_allocations.length > 0) {
      console.log("🔍 [OrderWorkflow Debug] Found device_allocations in order_data with length:", orderData.device_allocations.length);
      return true;
    }
    
    // For credit-only orders, allocation might not be required
    if (isCreditOnly) {
      console.log("🔍 [OrderWorkflow Debug] Credit-only order, checking if allocation is even needed");
      return true; // Credit-only orders don't need device allocation
    }
    
    console.log("🔍 [OrderWorkflow Debug] No valid device allocation found");
    return false;
  })();
  
  console.log("🔍 [OrderWorkflow Debug] Final isDeviceAllocationComplete:", isDeviceAllocationComplete);

  const generateDeliveryNotePDF = async () => {
    setIsGeneratingDeliveryNote(true);
    try {
      // Fetch the deal and customer data
      const { data: dealData, error: dealError } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(
            id,
            first_name,
            last_name,
            email,
            phone,
            organization:organizations(name, address)
          )
        `)
        .eq('id', order.sale?.deal_id)
        .single();

      if (dealError || !dealData) {
        toast({
          title: "Error", 
          description: "Failed to fetch customer details for delivery note",
          variant: "destructive",
        });
        return;
      }

      // Create PDF
      const doc = new jsPDF({ compress: true });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Load and add logo centered at top
      let currentY = 15;
      try {
        const logoImg = new Image();
        logoImg.src = '/snapshot-logo.png';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
        });

        // Use standardized canvas size to avoid distortion
        const standardCanvasSize = 200;
        const canvas = document.createElement('canvas');
        canvas.width = standardCanvasSize;
        canvas.height = standardCanvasSize;
        
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Calculate scaling to fit logo within canvas while maintaining aspect ratio
          const aspectRatio = logoImg.width / logoImg.height;
          let drawWidth = standardCanvasSize;
          let drawHeight = standardCanvasSize;
          
          if (aspectRatio > 1) {
            // Wider than tall
            drawHeight = standardCanvasSize / aspectRatio;
          } else {
            // Taller than wide
            drawWidth = standardCanvasSize * aspectRatio;
          }
          
          // Center the image in the canvas
          const offsetX = (standardCanvasSize - drawWidth) / 2;
          const offsetY = (standardCanvasSize - drawHeight) / 2;
          
          ctx.drawImage(logoImg, offsetX, offsetY, drawWidth, drawHeight);
          const logoDataUrl = canvas.toDataURL('image/png');
          
          // Render at reasonable size in PDF (40px)
          const pdfLogoSize = 40;
          const logoX = (pageWidth - pdfLogoSize) / 2;
          doc.addImage(logoDataUrl, 'PNG', logoX, currentY, pdfLogoSize, pdfLogoSize);
        }
        currentY = 60;
      } catch (error) {
        console.error('Failed to load logo:', error);
        // Fallback: add text header if logo fails
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('Snapshot Live', pageWidth / 2, currentY, { align: 'center' });
        currentY = 30;
      }
      
      // Header below logo
      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.text('DELIVERY NOTE', pageWidth / 2, currentY, { align: 'center' });
      
      // Document number and date
      currentY += 15;
      const documentNumber = `DN-${Date.now().toString().slice(-8)}`;
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Document Number: ${documentNumber}`, 20, currentY);
      currentY += 5;
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, currentY);
      currentY += 5;
      doc.text(`Order ID: ${order.id.slice(0, 8)}...`, 20, currentY);
      
      // Add customer PO/Order Reference if available
      if (order.order_reference) {
        currentY += 5;
        doc.text(`Customer PO: ${order.order_reference}`, 20, currentY);
      }
      
      // Customer Details
      currentY += 10;
      doc.setFont(undefined, 'bold');
      doc.text('DELIVERY TO:', 20, currentY);
      doc.setFont(undefined, 'normal');
      
      const customer = dealData.contact;
      let yPos = currentY + 10;
      if (customer.organization?.name) {
        doc.text(customer.organization.name, 20, yPos);
        yPos += 5;
      }
      doc.text(`${customer.first_name} ${customer.last_name}`, 20, yPos);
      yPos += 5;
      if (customer.email) {
        doc.text(`Email: ${customer.email}`, 20, yPos);
        yPos += 5;
      }
      if (customer.phone) {
        doc.text(`Phone: ${customer.phone}`, 20, yPos);
        yPos += 5;
      }
      if (customer.organization?.address) {
        doc.text(`Address: ${customer.organization.address}`, 20, yPos);
        yPos += 10;
      }
      
      // Order Details Table
      const tableData = [];
      
      if (!isCreditOnly) {
        // Add device details with serial numbers
        const dealDetails = dealData.deal_data as any || {};
        const cameraModelName = dealDetails.camera_model || 'Day & Night Vision Camera SSC200';
        const allocatedDevices = order.allocated_device_ids || [];

        // Look up friendly SSL serial numbers for the allocated devices
        const { data: deviceDetails } = await supabase
          .from('device_details')
          .select('device_id, device_serial')
          .in('device_id', allocatedDevices.length > 0 ? allocatedDevices : ['__none__']);
        const serialByDeviceId = new Map(
          (deviceDetails || []).map((d) => [d.device_id, d.device_serial])
        );

        allocatedDevices.forEach((deviceId: string, index: number) => {
          tableData.push([
            cameraModelName,
            '1',
            serialByDeviceId.get(deviceId) || deviceId || `DEVICE-${index + 1}`,
            'Included'
          ]);
        });
        
        if (allocatedDevices.length === 0) {
          // Fallback for cameras without specific device IDs
          for (let i = 0; i < order.total_cameras; i++) {
            tableData.push([
              cameraModelName,
              '1', 
              'TBD',
              'Included'
            ]);
          }
        }
      }
      
      // Add credits
      tableData.push([
        'Image Credits Package',
        '1',
        `${order.total_images?.toLocaleString()} credits`,
        'Digital Delivery'
      ]);

      // Add accessories from deal_data
      {
        const dealDetails = (dealData.deal_data as any) || {};
        const dealAccessories = dealDetails.accessories || [];
        if (Array.isArray(dealAccessories)) {
          dealAccessories.forEach((acc: any) => {
            if (acc.quantity > 0) {
              tableData.push([
                acc.name || 'Accessory',
                String(acc.quantity),
                acc.id || '-',
                'Included'
              ]);
            }
          });
        }
      }

      // Generate table
      (doc as any).autoTable({
        startY: yPos + 10,
        head: [['Item Description', 'Qty', 'Serial/Details', 'Notes']],
        body: tableData,
        styles: {
          fontSize: 9,
          cellPadding: 3
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: 'bold'
        }
      });
      
      // Signature section
      let signatureY = (doc as any).lastAutoTable.finalY + 20;
      
      // Calculate space requirements - optimized to fit on single page
      const signatureHeight = 40;
      const footerHeight = 20;
      const minBottomMargin = 10;
      const totalRequiredSpace = signatureHeight + footerHeight + minBottomMargin;
      
      // Check if we need a new page
      if (signatureY + totalRequiredSpace > pageHeight) {
        doc.addPage();
        signatureY = 20;
      }
      
      doc.setFont(undefined, 'bold');
      doc.text('DELIVERY CONFIRMATION', 20, signatureY);
      
      doc.setFont(undefined, 'normal');
      doc.text('Received by: ___________________________', 20, signatureY + 15);
      doc.text('Signature: _____________________________', 20, signatureY + 25);
      doc.text('Date: _________________', 20, signatureY + 35);
      
      // Notes section with controlled spacing
      const notesY = signatureY + 50;
      doc.text('Notes:', 20, notesY);
      doc.text('_________________________________________', 20, notesY + 8);
      doc.text('_________________________________________', 20, notesY + 16);
      
      // Company Footer - positioned with safe margin from notes
      const footerY = Math.max(notesY + 35, pageHeight - footerHeight);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      
      // Terms line
      doc.setFont(undefined, 'italic');
      doc.text('Subject to agreed Terms & Conditions', pageWidth / 2, footerY - 15, { align: 'center' });
      
      doc.setFont(undefined, 'normal');
      doc.text(COMPANY_NAME, pageWidth / 2, footerY - 5, { align: 'center' });
      doc.text(COMPANY_ADDRESS_LINES.join(', '), pageWidth / 2, footerY, { align: 'center' });
      
      // Create blob and save
      const pdfBlob = doc.output('blob');
      
      await createDeliveryNote.mutateAsync({
        orderId: order.id,
        pdfBlob,
        documentNumber
      });
      
    } catch (error) {
      console.error('Error generating delivery note:', error);
      toast({
        title: "Error",
        description: "Failed to generate delivery note. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingDeliveryNote(false);
    }
  };

  // Generate delivery note for a specific shipment
  const generateShipmentDeliveryNotePDF = async (shipment: OrderShipment) => {
    if (!shipment.allocated_device_ids || shipment.allocated_device_ids.length === 0) {
      toast({
        title: "No Devices Allocated",
        description: "This shipment has no devices allocated. Please allocate devices before generating a delivery note.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingShipmentDeliveryNote(shipment.id);
    try {
      // Fetch the deal and customer data (same as order delivery note)
      const { data: dealData, error: dealError } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(
            id,
            first_name,
            last_name,
            email,
            phone,
            organization:organizations(name, address)
          )
        `)
        .eq('id', order.sale?.deal_id)
        .single();

      if (dealError || !dealData) {
        toast({
          title: "Error", 
          description: "Failed to fetch customer details for delivery note",
          variant: "destructive",
        });
        return;
      }

      // Create PDF
      const doc = new jsPDF({ compress: true });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Load and add logo centered at top
      let currentY = 15;
      try {
        const logoImg = new Image();
        logoImg.src = '/snapshot-logo.png';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
        });

        const standardCanvasSize = 200;
        const canvas = document.createElement('canvas');
        canvas.width = standardCanvasSize;
        canvas.height = standardCanvasSize;
        
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const aspectRatio = logoImg.width / logoImg.height;
          let drawWidth = standardCanvasSize;
          let drawHeight = standardCanvasSize;
          
          if (aspectRatio > 1) {
            drawHeight = standardCanvasSize / aspectRatio;
          } else {
            drawWidth = standardCanvasSize * aspectRatio;
          }
          
          const offsetX = (standardCanvasSize - drawWidth) / 2;
          const offsetY = (standardCanvasSize - drawHeight) / 2;
          
          ctx.drawImage(logoImg, offsetX, offsetY, drawWidth, drawHeight);
          const logoDataUrl = canvas.toDataURL('image/png');
          
          const pdfLogoSize = 40;
          const logoX = (pageWidth - pdfLogoSize) / 2;
          doc.addImage(logoDataUrl, 'PNG', logoX, currentY, pdfLogoSize, pdfLogoSize);
        }
        currentY = 60;
      } catch (error) {
        console.error('Failed to load logo:', error);
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('Snapshot Live', pageWidth / 2, currentY, { align: 'center' });
        currentY = 30;
      }
      
      // Header with shipment number
      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.text(`DELIVERY NOTE - Shipment #${shipment.shipment_number}`, pageWidth / 2, currentY, { align: 'center' });
      
      // Document number includes shipment reference
      currentY += 15;
      const documentNumber = `DN-${Date.now().toString().slice(-8)}-S${shipment.shipment_number}`;
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Document Number: ${documentNumber}`, 20, currentY);
      currentY += 5;
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, currentY);
      currentY += 5;
      doc.text(`Order ID: ${order.id.slice(0, 8)}...`, 20, currentY);
      
      // Add customer PO/Order Reference if available
      if (order.order_reference) {
        currentY += 5;
        doc.text(`Customer PO: ${order.order_reference}`, 20, currentY);
      }
      
      // Add tracking info if available on shipment
      if (shipment.tracking_number) {
        currentY += 5;
        doc.text(`Tracking Number: ${shipment.tracking_number}`, 20, currentY);
        if (shipment.carrier) {
          doc.text(` (${shipment.carrier})`, 20 + doc.getTextWidth(`Tracking Number: ${shipment.tracking_number}`), currentY);
        }
      }
      
      // Customer Details
      currentY += 10;
      doc.setFont(undefined, 'bold');
      doc.text('DELIVERY TO:', 20, currentY);
      doc.setFont(undefined, 'normal');
      
      const customer = dealData.contact;
      let yPos = currentY + 10;
      if (customer?.organization?.name) {
        doc.text(customer.organization.name, 20, yPos);
        yPos += 5;
      }
      if (customer) {
        doc.text(`${customer.first_name || ''} ${customer.last_name || ''}`.trim(), 20, yPos);
        yPos += 5;
        if (customer.email) {
          doc.text(`Email: ${customer.email}`, 20, yPos);
          yPos += 5;
        }
        if (customer.phone) {
          doc.text(`Phone: ${customer.phone}`, 20, yPos);
          yPos += 5;
        }
        if (customer.organization?.address) {
          doc.text(`Address: ${customer.organization.address}`, 20, yPos);
          yPos += 10;
        }
      }
      
      // Order Details Table - Use shipment's allocated devices
      const tableData: string[][] = [];
      const dealDetails = dealData.deal_data as any || {};
      const cameraModelName = dealDetails.camera_model || 'Day & Night Vision Camera SSC200';
      
      // Look up friendly SSL serial numbers for the shipment's devices
      const { data: shipmentDeviceDetails } = await supabase
        .from('device_details')
        .select('device_id, device_serial')
        .in('device_id', shipment.allocated_device_ids);
      const serialByDeviceId = new Map(
        (shipmentDeviceDetails || []).map((d) => [d.device_id, d.device_serial])
      );

      // Add only the devices allocated to this shipment
      shipment.allocated_device_ids.forEach((deviceId: string, index: number) => {
        tableData.push([
          cameraModelName,
          '1',
          serialByDeviceId.get(deviceId) || deviceId || `DEVICE-${index + 1}`,
          'Included'
        ]);
      });
      
      // Add credits for this shipment if available
      if (shipment.allocated_credits && shipment.allocated_credits > 0) {
        tableData.push([
          'Image Credits Package',
          '1',
          `${shipment.allocated_credits.toLocaleString()} credits`,
          'Digital Delivery'
        ]);
      }

      // Add accessories on the first shipment to avoid duplication
      if (shipment.shipment_number === 1) {
        const dealAccessories = dealDetails.accessories || [];
        if (Array.isArray(dealAccessories)) {
          dealAccessories.forEach((acc: any) => {
            if (acc.quantity > 0) {
              tableData.push([
                acc.name || 'Accessory',
                String(acc.quantity),
                acc.id || '-',
                'Included'
              ]);
            }
          });
        }
      }

      // Generate table
      (doc as any).autoTable({
        startY: yPos + 10,
        head: [['Item Description', 'Qty', 'Serial/Details', 'Notes']],
        body: tableData,
        styles: {
          fontSize: 9,
          cellPadding: 3
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: 'bold'
        }
      });
      
      // Signature section
      let signatureY = (doc as any).lastAutoTable.finalY + 20;
      
      const signatureHeight = 40;
      const footerHeight = 20;
      const minBottomMargin = 10;
      const totalRequiredSpace = signatureHeight + footerHeight + minBottomMargin;
      
      if (signatureY + totalRequiredSpace > pageHeight) {
        doc.addPage();
        signatureY = 20;
      }
      
      doc.setFont(undefined, 'bold');
      doc.text('DELIVERY CONFIRMATION', 20, signatureY);
      
      doc.setFont(undefined, 'normal');
      doc.text('Received by: ___________________________', 20, signatureY + 15);
      doc.text('Signature: _____________________________', 20, signatureY + 25);
      doc.text('Date: _________________', 20, signatureY + 35);
      
      // Notes section
      const notesY = signatureY + 50;
      doc.text('Notes:', 20, notesY);
      doc.text('_________________________________________', 20, notesY + 8);
      doc.text('_________________________________________', 20, notesY + 16);
      
      // Company Footer
      const footerY = Math.max(notesY + 35, pageHeight - footerHeight);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      
      doc.setFont(undefined, 'italic');
      doc.text('Subject to agreed Terms & Conditions', pageWidth / 2, footerY - 15, { align: 'center' });
      
      doc.setFont(undefined, 'normal');
      doc.text(COMPANY_NAME, pageWidth / 2, footerY - 5, { align: 'center' });
      doc.text(COMPANY_ADDRESS_LINES.join(', '), pageWidth / 2, footerY, { align: 'center' });
      
      // Create blob and save
      const pdfBlob = doc.output('blob');
      
      await createDeliveryNote.mutateAsync({
        orderId: order.id,
        pdfBlob,
        documentNumber
      });

      // Trigger browser download
      const downloadUrl = URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = `DeliveryNote-Shipment${shipment.shipment_number}-${documentNumber}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadUrl);

      toast({
        title: "Success",
        description: `Delivery note for Shipment #${shipment.shipment_number} downloaded`,
      });
      
    } catch (error) {
      console.error('Error generating shipment delivery note:', error);
      toast({
        title: "Error",
        description: "Failed to generate delivery note. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingShipmentDeliveryNote(null);
    }
  };

  const handleSendOrderConfirmation = async () => {
    if (!order.sale?.deal_id) {
      toast({
        title: "Error",
        description: "Cannot send order confirmation: Deal information not found",
        variant: "destructive",
      });
      return;
    }

    setIsSendingEmail(true);
    try {
      // First fetch the deal and contact information
      const { data: dealData, error: dealError } = await supabase
        .from('crm_deals')
        .select(`
          id,
          title,
          contact_id,
          contact:profiles!crm_deals_contact_id_fkey(email)
        `)
        .eq('id', order.sale.deal_id)
        .single();

      if (dealError || !dealData?.contact?.email) {
        toast({
          title: "Error",
          description: "Cannot send order confirmation: Contact email not found",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.functions.invoke('send-order-confirmation', {
        body: {
          dealId: dealData.id,
          orderId: order.id,
          contactEmail: dealData.contact.email,
          orderReference: order.order_reference,
        }
      });

      if (error) {
        console.error('Failed to send order confirmation:', error);
        toast({
          title: "Error",
          description: "Failed to send order confirmation email",
          variant: "destructive",
        });
      } else {
        // Update database to track email sending
        const now = new Date().toISOString();
        const updateData: any = {
          order_confirmation_last_sent_at: now
        };
        
        // If this is the first time sending, set the first sent timestamp
        if (!order.order_confirmation_sent_at) {
          updateData.order_confirmation_sent_at = now;
        }

        await supabase
          .from('orders')
          .update(updateData)
          .eq('id', order.id);

        setEmailJustSent(true);
        
        // Reset the "just sent" state after 3 seconds
        setTimeout(() => setEmailJustSent(false), 3000);
        
        // Refresh the order data to show updated timestamps
        queryClient.invalidateQueries({ queryKey: ['order-workflow', orderId] });
        
        toast({
          title: "Success",
          description: "Order confirmation email sent successfully!",
        });
      }
    } catch (error) {
      console.error('Error sending order confirmation:', error);
      toast({
        title: "Error", 
        description: "Failed to send order confirmation email",
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getStepIcon = (step: any) => {
    if (step.status === 'completed') {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    }
    if (step.showButton) {
      return <Clock className="h-5 w-5 text-blue-600" />;
    }
    return <Circle className="h-5 w-5 text-gray-400" />;
  };

  const handleAdvanceWorkflow = async (action: string) => {
    try {
      const metadata = action === 'ship' && trackingNumber ? { trackingNumber } : undefined;
      await advanceWorkflow.mutateAsync({
        orderId,
        action: action as any,
        metadata
      });

      if (action === 'ship') {
        setTrackingNumber('');
      }

      queryClient.invalidateQueries({ queryKey: ['order-workflow', orderId] });
    } catch (error) {
      console.error("Error advancing workflow:", error);
    }
  };

  const handlePaymentReceived = async () => {
    if (order.sale?.id) {
      try {
        await updateSaleStatus.mutateAsync({
          saleId: order.sale.id,
          status: 'paid'
        });

        queryClient.invalidateQueries({ queryKey: ['order-workflow', orderId] });
      } catch (error) {
        console.error("Error updating payment status:", error);
      }
    }
  };

  const handleSkipAllocationToggle = async (checked: boolean) => {
    try {
      await updateSkipAllocation.mutateAsync({
        orderId,
        skipAllocation: checked
      });
    } catch (error) {
      console.error("Error updating skip allocation:", error);
    }
  };

  const handleDeviceAllocationSave = async (allocations: DeviceAllocation[]) => {
    try {
      // Save device allocations
      await saveDeviceAllocation.mutateAsync({
        orderId,
        allocations
      });

      // Auto-advance workflow to next step after successful allocation
      if (isCreditOnly) {
        await handleAdvanceWorkflow('complete_credit_processing');
      } else {
        await handleAdvanceWorkflow('start_fulfillment');
      }
    } catch (error) {
      console.error("Error in device allocation and workflow advancement:", error);
    }
  };

  const handleButtonClick = (step: any) => {
    switch (step.buttonAction) {
      case 'mark_payment_received':
        handlePaymentReceived();
        break;
      case 'start_fulfillment':
        // Only allow if device allocation is complete (when required)
        if (!showDeviceAllocation || isDeviceAllocationComplete) {
          handleAdvanceWorkflow('start_fulfillment');
        }
        break;
      case 'complete_credit_processing':
        // For credit-only orders, allow completion if allocation is skipped or complete
        if (!showDeviceAllocation || isDeviceAllocationComplete) {
          handleAdvanceWorkflow('complete_credit_processing');
        }
        break;
      case 'ready_to_ship':
        handleAdvanceWorkflow('ready_to_ship');
        break;
      case 'ship':
        handleAdvanceWorkflow('ship');
        break;
      case 'deliver':
        handleAdvanceWorkflow('deliver');
        break;
      case 'mark_complete':
        handleAdvanceWorkflow('mark_complete');
        break;
    }
  };

  const getOrderStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'waiting_for_payment':
        return 'bg-yellow-100 text-yellow-800';
      case 'order_processing':
        return 'bg-blue-100 text-blue-800';
      case 'in_fulfillment':
        return 'bg-purple-100 text-purple-800';
      case 'ready_for_shipping':
        return 'bg-indigo-100 text-indigo-800';
      case 'shipped':
        return 'bg-green-100 text-green-800';
      case 'delivered':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOrderStatusLabel = (status: string) => {
    switch (status) {
      case 'waiting_for_payment':
        return 'Awaiting Payment';
      case 'order_processing':
        return isCreditOnly ? 'Processing Credits' : 'Processing';
      case 'in_fulfillment':
        return 'In Fulfillment';
      case 'ready_for_shipping':
        return 'Ready for Shipping';
      case 'shipped':
        return 'Shipped';
      case 'delivered':
        return isCreditOnly ? 'Credits Delivered' : 'Delivered';
      default:
        return status.replace('_', ' ');
    }
  };

  const getOrderTypeIcon = () => {
    return isCreditOnly ? <CreditCard className="h-5 w-5" /> : <Package className="h-5 w-5" />;
  };

  const getOrderTypeLabel = () => {
    return isCreditOnly ? 'Credit Order' : 'Camera & Credit Order';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-6">
            <DialogTitle className="flex items-center gap-2">
              {getOrderTypeIcon()}
              {getOrderTypeLabel()} - {orderId.slice(0, 8)}
            </DialogTitle>
            {isSystemAdmin && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
                className="gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete order
              </Button>
            )}
          </div>
        </DialogHeader>

        <AlertDialog open={deleteDialogOpen} onOpenChange={(o) => { setDeleteDialogOpen(o); if (!o) setDeleteConfirmText(''); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this order?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2">
                  <p>This permanently removes:</p>
                  <ul className="list-disc pl-5 text-sm">
                    <li>The order and its delivery notes / shipments</li>
                    <li>All credit packages tied to this order</li>
                    <li>The associated sale and its payments (if no other orders reference it)</li>
                  </ul>
                  <p className="text-sm">The CRM deal is preserved for audit history.</p>
                  <p className="text-sm">
                    Type <span className="font-mono font-semibold">{orderId.slice(0, 8)}</span> to confirm:
                  </p>
                  <Input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={orderId.slice(0, 8)}
                    autoFocus
                  />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); handleDeleteOrder(); }}
                disabled={isDeleting || deleteConfirmText !== orderId.slice(0, 8)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? 'Deleting…' : 'Delete order'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="space-y-6">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Order Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {!isCreditOnly && (
                  <div>
                    <span className="text-muted-foreground">Cameras:</span>
                    <p className="font-medium">{order.total_cameras}</p>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Credits:</span>
                  <p className="font-medium">{order.total_images?.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <Badge className={getOrderStatusBadgeColor(order.status)}>
                    {getOrderStatusLabel(order.status)}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Total:</span>
                  <p className="font-medium">£{order.sale?.total_amount}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Delivery Address */}
          {(order.delivery_address_record || order.delivery_address) && (
            <Card>
              <CardContent className="pt-6">
                <DeliveryAddressBlock
                  address={order.delivery_address_record}
                  legacyAddress={order.delivery_address}
                  title="Delivery Address"
                />
              </CardContent>
            </Card>
          )}


          {/* Test Order Confirmation Email */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Order Confirmation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button 
                  onClick={handleSendOrderConfirmation}
                  disabled={isSendingEmail}
                  className="flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
                  style={{ 
                    backgroundColor: emailJustSent ? '#4ade80' : '#DFEF97', 
                    color: emailJustSent ? 'white' : 'black',
                    border: `1px solid ${emailJustSent ? '#4ade80' : '#DFEF97'}`
                  }}
                >
                  {emailJustSent ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Email Sent!
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      {isSendingEmail ? 'Sending...' : (order.order_confirmation_sent_at ? 'Resend Order Confirmation' : 'Send Order Confirmation')}
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={() => generateOrderSummaryPDF(order)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <FileDown className="h-4 w-4" />
                  Order Summary
                </Button>
              </div>
              
              <div className="mt-3 space-y-2">
                <p className="text-sm text-muted-foreground">
                  This will send an order confirmation email with PDF to the customer
                </p>
                
                {order.order_confirmation_sent_at && (
                  <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                    <CheckCircle2 className="h-3 w-3" />
                    <span>
                      First sent: {new Date(order.order_confirmation_sent_at).toLocaleString()}
                      {order.order_confirmation_last_sent_at && order.order_confirmation_last_sent_at !== order.order_confirmation_sent_at && (
                        <> | Last sent: {new Date(order.order_confirmation_last_sent_at).toLocaleString()}</>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>


          {/* Skip Allocation Toggle - Only show during order processing for camera orders */}
          {order.status === 'order_processing' && !isCreditOnly && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Allocation Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="skip-allocation"
                    checked={order.skip_device_allocation || false}
                    onCheckedChange={handleSkipAllocationToggle}
                    disabled={updateSkipAllocation.isPending}
                  />
                  <Label htmlFor="skip-allocation" className="text-sm font-medium">
                    Skip Device Allocation
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {order.skip_device_allocation 
                    ? "Customer will allocate credits to devices themselves in their Credit Management section"
                    : "Credits will be allocated to specific devices during order processing"
                  }
                </p>
              </CardContent>
            </Card>
          )}

          {/* Workflow Steps */}
          <Card>
            <CardHeader>
              <CardTitle>Workflow Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {steps.map(step => (
                  <div key={step.id}>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        {getStepIcon(step)}
                        <div>
                          <h4 className="font-medium">{step.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {step.description}
                          </p>
                          {step.timestamp && (
                            <p className="text-xs text-muted-foreground">
                              {new Date(step.timestamp).toLocaleString()}
                            </p>
                          )}
                          {step.id === 'shipping' && order.tracking_number && (
                            <p className="text-xs text-blue-600">
                              Tracking: {order.tracking_number}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 items-center">
                        {/* Hide ship/deliver buttons when using multi-shipment workflow */}
                        {step.showButton && !(hasShipments && (step.buttonAction === 'ship' || step.buttonAction === 'deliver')) && (
                          <div className="flex items-center gap-2">
                            {step.buttonAction === 'ship' && (
                              <div className="flex flex-col gap-1">
                                <Input
                                  id="tracking-number"
                                  type="text"
                                  placeholder="Enter tracking number"
                                  value={trackingNumber}
                                  onChange={(e) => setTrackingNumber(e.target.value)}
                                  className="w-60 h-8 text-xs"
                                />
                              </div>
                            )}
                            <div className="flex flex-col items-end gap-1">
                              <Button
                                size="sm"
                                onClick={() => handleButtonClick(step)}
                                disabled={
                                  advanceWorkflow.isPending || 
                                  updateSaleStatus.isPending || 
                                  (step.buttonAction === 'ship' && !trackingNumber.trim()) ||
                                  ((step.buttonAction === 'start_fulfillment' || step.buttonAction === 'complete_credit_processing') && 
                                   showDeviceAllocation && !isDeviceAllocationComplete)
                                }
                                style={{ 
                                  backgroundColor: '#DFEF97', 
                                  color: 'black',
                                  border: '1px solid #DFEF97'
                                }}
                                className="hover:opacity-90 disabled:opacity-50"
                              >
                                {step.buttonText}
                              </Button>
                              {((step.buttonAction === 'start_fulfillment' || step.buttonAction === 'complete_credit_processing') && 
                                showDeviceAllocation && !isDeviceAllocationComplete) && (
                                <p className="text-xs text-orange-600">
                                  Complete device allocation first
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Show hint when using shipment workflow */}
                        {hasShipments && (step.buttonAction === 'ship' || step.buttonAction === 'deliver') && step.showButton && (
                          <p className="text-xs text-muted-foreground italic">
                            Use shipment actions below
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Optional Delivery Note Generation - Shows at Ready for Shipping and after */}
                    {step.id === 'shipping' && (order.status === 'ready_for_shipping' || order.status === 'shipped' || order.status === 'delivered') && (
                      <div className="mt-4 ml-11 p-4 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/50">
                        <div className="flex items-center gap-2 mb-3">
                          <FileDown className="h-4 w-4 text-gray-500" />
                          <h5 className="font-medium text-sm">Delivery Documentation (Optional)</h5>
                          <Badge variant="outline" className="text-xs">Optional</Badge>
                        </div>
                        
                        <div className="space-y-3">
                          {!deliveryNotes || deliveryNotes.length === 0 ? (
                            <div className="flex flex-col gap-2">
                              <Button 
                                onClick={generateDeliveryNotePDF}
                                disabled={isGeneratingDeliveryNote}
                                size="sm"
                                variant="outline"
                                className="w-fit flex items-center gap-2"
                              >
                                <FileDown className="h-3 w-3" />
                                {isGeneratingDeliveryNote ? 'Generating...' : 'Generate Delivery Note'}
                              </Button>
                              <p className="text-xs text-muted-foreground">
                                Generate a delivery note with device serial numbers and signature space for physical delivery confirmation.
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between p-2 bg-white border rounded">
                                <div>
                                  <p className="text-sm font-medium">{deliveryNotes[0].document_number}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Generated: {new Date(deliveryNotes[0].generated_at || deliveryNotes[0].created_at).toLocaleString()}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {deliveryNotes[0].pdf_url && (
                                    <a
                                      href={deliveryNotes[0].pdf_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-xs font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground h-7 px-2 border"
                                    >
                                      <FileDown className="h-3 w-3" />
                                      Download
                                    </a>
                                  )}
                                  <Button 
                                    onClick={generateDeliveryNotePDF}
                                    disabled={isGeneratingDeliveryNote}
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                  >
                                    Regenerate
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Manual Order Completion - Safety net for race condition cases */}
          {hasShipments && 
           shipments.every(s => s.status === 'confirmed') && 
           order.fulfillment_status !== 'completed' && (
            <Card className="border-amber-300 bg-amber-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="h-5 w-5" />
                  Order Completion Required
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-amber-700 mb-4">
                  All shipments have been confirmed, but the order status wasn't automatically updated. 
                  Click the button below to complete this order.
                </p>
                <Button
                  onClick={() => {
                    advanceWorkflow.mutate({
                      orderId: orderId,
                      action: 'mark_complete'
                    });
                  }}
                  disabled={advanceWorkflow.isPending}
                  style={{ 
                    backgroundColor: '#DFEF97', 
                    color: 'black',
                    border: '1px solid #DFEF97'
                  }}
                  className="hover:opacity-90"
                >
                  {advanceWorkflow.isPending ? 'Completing...' : 'Complete Order'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Device Allocation Section - Hide when using multi-shipment path */}
          {showDeviceAllocation && !hasShipments && (
            <DeviceAllocationSection
              orderId={orderId}
              totalCameras={order.total_cameras}
              totalImages={order.total_images}
              isExpanded={isDeviceAllocationExpanded}
              onToggle={() => setIsDeviceAllocationExpanded(!isDeviceAllocationExpanded)}
              onSave={handleDeviceAllocationSave}
            />
          )}

          {/* Multi-Shipment Management - Show from order_processing for incremental allocation */}
          {!isCreditOnly && (order.status === 'order_processing' || order.status === 'in_fulfillment' || order.status === 'ready_for_shipping' || order.status === 'shipped' || order.status === 'delivered') && (
            <ShipmentManagementSection
              orderId={orderId}
              allocatedDeviceIds={order.allocated_device_ids || []}
              totalCredits={order.total_images || 0}
              orderStatus={order.status}
              onGenerateDeliveryNote={(shipment: OrderShipment) => {
                generateShipmentDeliveryNotePDF(shipment);
              }}
              isGeneratingDeliveryNoteForShipment={isGeneratingShipmentDeliveryNote}
            />
          )}

          {/* Credit Package Creation Section */}
          {order.status === 'delivered' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Credit Package Management</CardTitle>
              </CardHeader>
              <CardContent>
                <CreateCreditPackageButton order={order} />
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
